// src/pages/PartnerEmpresaComissoes.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../contexts/firebase/config";
import { collection, query, where, onSnapshot, Timestamp } from "firebase/firestore";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
  Avatar,
  Stack,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";

import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import AutoGraphRoundedIcon from "@mui/icons-material/AutoGraphRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { Link as RouterLink } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import sintanetLogo from "../assets/sintanet-logo.png";

function formatMoney(value) {
  return `${Number(value || 0).toFixed(2)} €`;
}

export default function PartnerEmpresaComissoes() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const colors = {
    accent: "#ff6a00",
    success: "#ff8a1f",
    warning: theme.palette.warning.main,
    card: isDark ? "rgba(15,23,42,0.9)" : "rgba(255,255,255,0.94)",
    border: isDark ? "rgba(148,163,184,0.16)" : "rgba(15,23,42,0.08)",
    text: theme.palette.text.primary,
    muted: theme.palette.text.secondary,
    hero: "#080808",
  };

  const { currentUser } = useAuth();
  const empresaId = currentUser?.uid;
  const tableRef = useRef();

  const anoAtual = new Date().getFullYear();
  const anosDisponiveis = ["todos", anoAtual - 1, anoAtual, anoAtual + 1];

  const [selectedYear, setSelectedYear] = useState("todos");
  const [comissoes, setComissoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totais, setTotais] = useState({ total: 0, pendente: 0, pago: 0 });

  useEffect(() => {
    if (!empresaId) return;

    setLoading(true);

    let q;

    if (selectedYear === "todos") {
      q = query(collection(db, "compras"), where("empresaId", "==", empresaId));
    } else {
      const anoNum = parseInt(selectedYear, 10);
      const inicio = Timestamp.fromDate(new Date(anoNum, 0, 1));
      const fim = Timestamp.fromDate(new Date(anoNum + 1, 0, 1));

      q = query(
        collection(db, "compras"),
        where("empresaId", "==", empresaId),
        where("dataCompra", ">=", inicio),
        where("dataCompra", "<", fim)
      );
    }

    const unsub = onSnapshot(
      q,
      (snap) => {
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        let total = 0;
        let pendente = 0;
        let pago = 0;

        docs.forEach((c) => {
          const comissao = Number(c.comissao || 0);

          total += comissao;

          if (c.status === "Pago") pago += comissao;
          else pendente += comissao;
        });

        setComissoes(docs);
        setTotais({ total, pendente, pago });
        setLoading(false);
      },
      () => setLoading(false)
    );

    return unsub;
  }, [empresaId, selectedYear]);

  const paidPercentage = useMemo(() => {
    if (!totais.total) return 0;
    return Math.round((totais.pago / totais.total) * 100);
  }, [totais.total, totais.pago]);

  const handleExportPDF = async () => {
    if (!tableRef.current) return;

    const canvas = await html2canvas(tableRef.current, { scale: 2 });
    const img = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const width = pdf.internal.pageSize.getWidth();
    const height = (canvas.height * width) / canvas.width;

    pdf.addImage(img, "PNG", 0, 0, width, height);
    pdf.save(`comissoes-${selectedYear}.pdf`);
  };

  const TotalCard = ({ label, value, icon, helper, color }) => (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: "26px",
        border: `1px solid ${colors.border}`,
        bgcolor: colors.card,
        overflow: "hidden",
        position: "relative",
        boxShadow: isDark
          ? "0 20px 55px rgba(0,0,0,0.28)"
          : "0 18px 45px rgba(15,23,42,0.07)",
        transition: "0.22s ease",
        "&:hover": {
          transform: "translateY(-3px)",
          borderColor: alpha(color, 0.35),
        },
        "&:before": {
          content: '""',
          position: "absolute",
          width: 180,
          height: 180,
          right: -80,
          bottom: -90,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(
            color,
            isDark ? 0.18 : 0.12
          )} 0%, transparent 70%)`,
        },
      }}
    >
      <CardContent sx={{ p: 2.7, position: "relative", zIndex: 1 }}>
        <Stack spacing={2.3}>
          <Avatar
            sx={{
              width: 54,
              height: 54,
              borderRadius: "19px",
              bgcolor: alpha(color, isDark ? 0.18 : 0.1),
              color,
            }}
          >
            {icon}
          </Avatar>

          <Box>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 950,
                color: colors.muted,
                textTransform: "uppercase",
                letterSpacing: "0.12em",
              }}
            >
              {label}
            </Typography>

            <Typography
              sx={{
                mt: 1,
                fontSize: { xs: "2rem", md: "2.3rem" },
                fontWeight: 950,
                letterSpacing: "-0.07em",
                lineHeight: 1,
                color: colors.text,
              }}
            >
              {value}
            </Typography>

            <Typography sx={{ mt: 0.8, color: colors.muted, fontSize: 14 }}>
              {helper}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 76px)",
        width: "100%",
        position: "relative",
        px: { xs: 1.5, sm: 2.5, md: 3.5 },
        py: { xs: 2, md: 3 },
        background: isDark
          ? "linear-gradient(135deg, #020617 0%, #0f172a 55%, #111827 100%)"
          : "linear-gradient(135deg, #f8fafc 0%, #fff7ed 48%, #f8fafc 100%)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          opacity: isDark ? 0.16 : 0.28,
          backgroundImage:
            "linear-gradient(rgba(148,163,184,0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.16) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage: "linear-gradient(to bottom, black, transparent 72%)",
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: "1520px",
          mx: "auto",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: { xs: 2.4, md: 3.6 },
            borderRadius: "32px",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.14)",
            overflow: "hidden",
            position: "relative",
            boxShadow: isDark
              ? "0 26px 80px rgba(0,0,0,0.34)"
              : "0 24px 70px rgba(15,23,42,0.2)",
            background:
              "radial-gradient(circle at 82% 28%, rgba(255,106,0,0.42) 0%, rgba(255,106,0,0.18) 24%, transparent 48%), linear-gradient(135deg, #05070b 0%, #121212 52%, #ff6a00 180%)",
            "&:before": {
              content: '""',
              position: "absolute",
              inset: 0,
              backgroundImage: `url(${sintanetLogo})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: {
                xs: "center 22px",
                lg: "right 60px center",
              },
              backgroundSize: {
                xs: "260px auto",
                md: "420px auto",
                lg: "520px auto",
              },
              opacity: 0.12,
              filter: "drop-shadow(0 24px 50px rgba(255,106,0,0.35))",
              pointerEvents: "none",
            },
          }}
        >
          <Grid container spacing={3} alignItems="center" sx={{ position: "relative", zIndex: 1 }}>
            <Grid item xs={12} lg={8}>
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
                <Avatar
                  src={sintanetLogo}
                  variant="rounded"
                  sx={{
                    width: 54,
                    height: 54,
                    borderRadius: "18px",
                    bgcolor: "rgba(255,106,0,0.16)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    p: 0.8,
                    "& img": { objectFit: "contain" },
                  }}
                />

                <Typography
                  variant="overline"
                  sx={{
                    fontSize: 12,
                    fontWeight: 900,
                    letterSpacing: "0.18em",
                    color: "rgba(255,255,255,0.72)",
                  }}
                >
                  Minhas Comissões
                </Typography>
              </Stack>

              <Typography
                sx={{
                  fontSize: { xs: "2.15rem", sm: "3rem", md: "4rem" },
                  fontWeight: 950,
                  lineHeight: 0.95,
                  letterSpacing: "-0.08em",
                  color: "#fff",
                }}
              >
                Relatório de Comissões
              </Typography>

              <Typography
                sx={{
                  mt: 1.7,
                  maxWidth: 820,
                  color: "rgba(255,255,255,0.72)",
                  fontSize: { xs: "0.98rem", md: "1.08rem" },
                  lineHeight: 1.75,
                }}
              >
                Acompanha valores pagos, pendentes e exporta o relatório das tuas
                comissões num painel moderno com a identidade visual da Sintanet.
              </Typography>

              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2.4 }}>
                <Chip
                  icon={<WorkspacePremiumRoundedIcon />}
                  label="Área Parceiro"
                  sx={{
                    height: 36,
                    borderRadius: 999,
                    fontWeight: 900,
                    color: "#fff",
                    bgcolor: "rgba(255,255,255,0.1)",
                    border: "1px solid rgba(255,255,255,0.16)",
                    "& .MuiChip-icon": { color: "#ff8a1f" },
                  }}
                />

                <Chip
                  label={selectedYear === "todos" ? "Todos os anos" : selectedYear}
                  sx={{
                    height: 36,
                    borderRadius: 999,
                    fontWeight: 900,
                    color: "#fff",
                    bgcolor: "rgba(255,106,0,0.26)",
                    border: "1px solid rgba(255,255,255,0.16)",
                  }}
                />
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ mt: 3 }}>
                <Button
                  component={RouterLink}
                  to="/dashboard"
                  variant="contained"
                  startIcon={<ArrowBackIcon />}
                  sx={{
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 950,
                    px: 2.4,
                    bgcolor: "#ff6a00",
                    color: "#fff",
                    boxShadow: "0 14px 35px rgba(255,106,0,0.35)",
                    "&:hover": { bgcolor: "#ff7a1a" },
                  }}
                >
                  Voltar à Dashboard
                </Button>

                <Button
                  variant="outlined"
                  onClick={handleExportPDF}
                  disabled={loading || comissoes.length === 0}
                  startIcon={<FileDownloadRoundedIcon />}
                  sx={{
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 950,
                    px: 2.4,
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.28)",
                    "&:hover": {
                      borderColor: "#ff6a00",
                      bgcolor: "rgba(255,106,0,0.12)",
                    },
                  }}
                >
                  Exportar PDF
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.2,
                  borderRadius: "26px",
                  bgcolor: "rgba(10,10,10,0.48)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  backdropFilter: "blur(18px)",
                }}
              >
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1.3} alignItems="center">
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "18px",
                        bgcolor: "rgba(255,106,0,0.22)",
                        color: "#fff",
                      }}
                    >
                      <AutoGraphRoundedIcon />
                    </Avatar>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: "rgba(255,255,255,0.66)",
                          fontWeight: 800,
                        }}
                      >
                        Progresso financeiro
                      </Typography>

                      <Typography sx={{ fontSize: "1.25rem", fontWeight: 950 }}>
                        {loading ? "…" : `${paidPercentage}% liquidado`}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack spacing={1.1}>
                    <FormControl
                      size="small"
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: 44,
                          borderRadius: "16px",
                          color: "#fff",
                          bgcolor: "rgba(255,255,255,0.1)",
                          "& fieldset": { borderColor: "rgba(255,255,255,0.18)" },
                          "&:hover fieldset": { borderColor: "rgba(255,255,255,0.32)" },
                          "&.Mui-focused fieldset": { borderColor: "#ff6a00" },
                        },
                        "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.72)" },
                        "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.72)" },
                      }}
                    >
                      <InputLabel>Ano</InputLabel>
                      <Select
                        value={selectedYear}
                        label="Ano"
                        onChange={(e) => setSelectedYear(e.target.value)}
                      >
                        {anosDisponiveis.map((y) => (
                          <MenuItem key={y} value={y}>
                            {y === "todos" ? "Todos" : y}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>

                  <Divider sx={{ borderColor: "rgba(255,255,255,0.14)" }} />

                  {[
                    ["Total", totais.total],
                    ["Pago", totais.pago],
                    ["Pendente", totais.pendente],
                  ].map(([label, value]) => (
                    <Stack key={label} direction="row" justifyContent="space-between">
                      <Typography
                        sx={{
                          color: "rgba(255,255,255,0.64)",
                          fontWeight: 800,
                        }}
                      >
                        {label}
                      </Typography>

                      <Typography sx={{ color: "#fff", fontWeight: 950 }}>
                        {formatMoney(value)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: colors.accent }} />
          </Box>
        ) : (
          <>
            <Grid container spacing={2.4} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <TotalCard
                  label="Total"
                  value={formatMoney(totais.total)}
                  icon={<MonetizationOnIcon />}
                  helper="Comissão acumulada"
                  color={colors.accent}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TotalCard
                  label="Pendente"
                  value={formatMoney(totais.pendente)}
                  icon={<HourglassEmptyIcon />}
                  helper="Ainda por liquidar"
                  color={colors.warning}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TotalCard
                  label="Pago"
                  value={formatMoney(totais.pago)}
                  icon={<CheckCircleIcon />}
                  helper="Comissões pagas"
                  color={colors.success}
                />
              </Grid>
            </Grid>

            <Paper
              elevation={0}
              ref={tableRef}
              sx={{
                borderRadius: "30px",
                border: `1px solid ${colors.border}`,
                bgcolor: colors.card,
                boxShadow: isDark
                  ? "0 24px 64px rgba(0,0,0,0.28)"
                  : "0 24px 60px rgba(15,23,42,0.07)",
                overflow: "hidden",
              }}
            >
              <Box sx={{ px: { xs: 2.25, md: 3 }, py: 2.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                  <Stack direction="row" spacing={1.45} alignItems="center" sx={{ minWidth: 0 }}>
                    <Avatar
                      sx={{
                        width: 46,
                        height: 46,
                        borderRadius: "17px",
                        bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
                        color: colors.accent,
                      }}
                    >
                      <ReceiptLongRoundedIcon />
                    </Avatar>

                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" fontWeight={950} sx={{ color: colors.text }}>
                        Lista de Comissões
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                        {comissoes.length} registo(s) encontrado(s).
                      </Typography>
                    </Box>
                  </Stack>

                  <Chip
                    label={selectedYear === "todos" ? "Todos os anos" : selectedYear}
                    sx={{
                      flexShrink: 0,
                      borderRadius: "999px",
                      fontWeight: 900,
                      bgcolor: alpha(colors.accent, isDark ? 0.16 : 0.08),
                      color: colors.accent,
                    }}
                  />
                </Stack>
              </Box>

              <Divider sx={{ borderColor: colors.border }} />

              <TableContainer component={Box} sx={{ bgcolor: "transparent" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        "& th": {
                          bgcolor: isDark ? "rgba(2,6,23,0.38)" : "rgba(248,250,252,0.92)",
                          fontWeight: 900,
                          color: colors.muted,
                          borderBottom: `1px solid ${colors.border}`,
                          fontSize: "0.76rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          py: 1.45,
                        },
                      }}
                    >
                      <TableCell>Cliente</TableCell>
                      <TableCell>Ano</TableCell>
                      <TableCell align="right">Valor</TableCell>
                      <TableCell align="right">Comissão</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {comissoes.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          align="center"
                          sx={{ py: 5, color: colors.muted, borderBottom: 0 }}
                        >
                          Nenhuma comissão{" "}
                          {selectedYear === "todos" ? "" : `em ${selectedYear}`} encontrada.
                        </TableCell>
                      </TableRow>
                    ) : (
                      comissoes.map((c) => {
                        const dt = c.dataCompra?.toDate?.();
                        const ano = dt ? dt.getFullYear() : "—";
                        const pago = c.status === "Pago";

                        return (
                          <TableRow
                            key={c.id}
                            hover
                            sx={{
                              "& td": {
                                borderBottom: `1px solid ${colors.border}`,
                                py: 1.7,
                              },
                              "&:last-child td": { borderBottom: 0 },
                              "&:hover": {
                                bgcolor: alpha(colors.accent, isDark ? 0.08 : 0.045),
                              },
                            }}
                          >
                            <TableCell>
                              <Stack direction="row" spacing={1.2} alignItems="center">
                                <Avatar
                                  sx={{
                                    width: 34,
                                    height: 34,
                                    fontSize: 14,
                                    fontWeight: 900,
                                    bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
                                    color: colors.accent,
                                  }}
                                >
                                  <PersonRoundedIcon fontSize="small" />
                                </Avatar>

                                <Typography sx={{ fontWeight: 850, color: colors.text }}>
                                  {c.clienteNome || "—"}
                                </Typography>
                              </Stack>
                            </TableCell>

                            <TableCell sx={{ color: colors.muted }}>{ano}</TableCell>

                            <TableCell align="right" sx={{ color: colors.text, fontWeight: 750 }}>
                              {typeof c.valor === "number" ? formatMoney(c.valor) : "—"}
                            </TableCell>

                            <TableCell align="right">
                              <Chip
                                size="small"
                                label={typeof c.comissao === "number" ? formatMoney(c.comissao) : "—"}
                                sx={{
                                  borderRadius: "999px",
                                  fontWeight: 900,
                                  bgcolor: alpha(colors.accent, isDark ? 0.16 : 0.08),
                                  color: colors.accent,
                                }}
                              />
                            </TableCell>

                            <TableCell>
                              <Chip
                                size="small"
                                label={c.status || "Pendente"}
                                sx={{
                                  borderRadius: "999px",
                                  fontWeight: 900,
                                  bgcolor: pago
                                    ? alpha(colors.success, isDark ? 0.18 : 0.1)
                                    : alpha(colors.warning, isDark ? 0.2 : 0.12),
                                  color: pago ? colors.success : colors.warning,
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        )}
      </Box>
    </Box>
  );
}